import { currentTokens, loginRedirect } from './auth';

// ---------- Runway snapshot — matches lambda/shared/runway.ts ----------
export interface RunwaySnapshot {
  liquid_cash: number;
  monthly_burn: number;
  w2_monthly_net: number;
  expected_severance_months: number;
  consulting_target: number;
  saas_revenue_target: number;
  verified_income_90d: number;
  account_count: number;
  scenarios: Record<string, RunwayScenario>;
  _sources: Record<string, 'wealth' | 'override' | 'fallback'>;
  _warnings?: string[];
}

export interface RunwayScenario {
  name: string;
  headline: string;
  narrative: string;
  monthly_in: number;
  monthly_out: number;
  net: number;
  /** Number.POSITIVE_INFINITY when net is non-negative. JSON serializes as `null`. */
  months_runway: number | null;
  tone: 'ok' | 'watch' | 'warn';
  notes: string[];
}

// ---------- Chat ----------
export interface ChatRequest {
  message: string;
  conversationId?: string;
  model?: string;
}

export interface ChatResponse {
  conversationId: string;
  model: string;
  recommended: string;
  message: string;
  usage?: unknown;
}

// ---------- Pipeline / Projects / Decisions / Overrides ----------
// Field shapes mirror reference/atelier.jsx so the UI doesn't need translation.

export interface PipelineRow {
  id: string;
  source: string;
  kind: string;
  value: number;
  prob: number;
  when: string;
  next: string;
  tone: 'ok' | 'watch' | 'warn';
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectRow {
  id: string;
  name: string;
  tier: 'production' | 'commercial' | 'platform';
  status: string;
  mrr?: number;
  customer: string;
  stack: string;
  next: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DecisionRow {
  id: string;
  date: string;
  title: string;
  reasoning: string;
  verdict: string;
  tone: 'firm' | 'pending' | 'warn';
  createdAt?: string;
  updatedAt?: string;
}

export interface OverrideRow {
  field: string;
  value: number;
  note?: string;
  updatedAt?: string;
}

// ---------- Generic auth-aware fetch ----------
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const tokens = await currentTokens();
  if (!tokens) {
    await loginRedirect();
    throw new Error('redirecting to login'); // unreachable — loginRedirect navigates away
  }
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${tokens.idToken}`);
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');

  const res = await fetch(path, { ...init, headers });
  if (res.status === 401) {
    await loginRedirect();
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${init.method ?? 'GET'} ${path} failed: ${res.status} ${text}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ---------- Runway ----------
export const getRunwaySnapshot = (): Promise<RunwaySnapshot> =>
  request<RunwaySnapshot>('/api/runway-snapshot');

// ---------- Chat ----------
export const sendChat = (body: ChatRequest): Promise<ChatResponse> =>
  request<ChatResponse>('/api/chat', { method: 'POST', body: JSON.stringify(body) });

// ---------- Generic CRUD factory ----------
function crudClient<T extends { id: string }>(prefix: string) {
  return {
    list: () => request<{ items: T[] }>(prefix).then((r) => r.items),
    get: (id: string) => request<T>(`${prefix}/${encodeURIComponent(id)}`),
    create: (body: Omit<T, 'id' | 'createdAt' | 'updatedAt'> & Partial<Pick<T, 'id'>>) =>
      request<T>(prefix, { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<T>) =>
      request<T>(`${prefix}/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    remove: (id: string) =>
      request<void>(`${prefix}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  };
}

export const pipelineApi = crudClient<PipelineRow>('/api/pipeline');
export const projectsApi = crudClient<ProjectRow>('/api/projects');
export const decisionsApi = crudClient<DecisionRow>('/api/decisions');

// ---------- Financial overrides — single-row by `field` ----------
export const overridesApi = {
  list: () => request<{ items: OverrideRow[] }>('/api/financial-overrides').then((r) => r.items),
  set: (field: string, value: number, note?: string) =>
    request<OverrideRow>(`/api/financial-overrides/${encodeURIComponent(field)}`, {
      method: 'PUT',
      body: JSON.stringify({ field, value, note }),
    }),
  clear: (field: string) =>
    request<void>(`/api/financial-overrides/${encodeURIComponent(field)}`, { method: 'DELETE' }),
};
